import { buildQuote } from "./utils.js";
export function summarizeCategory(cat) {
    const p = cat.population;
    const families = p.M + p.Mplus1 + p.Mplus2 + p.Mplus3 + p.Mplus4 + p.Mplus5;
    const lives = p.M * 1 +
        p.Mplus1 * 2 +
        p.Mplus2 * 3 +
        p.Mplus3 * 4 +
        p.Mplus4 * 5 +
        p.Mplus5 * 6 +
        p.additionalDependents;
    const principalsPlusSpouses = families + cat.limits.numberWithSpouses;
    return { families, lives, principalsPlusSpouses };
}
/**
 * Look up premium from a table (rates[limitIndex][configIndex]).
 * Returns 0 when chosenLimit is 0 (benefit not selected).
 * Uses the next-higher limit when an exact match is not in the table.
 */
function tableRate(limits, rates, chosenLimit, configIndex) {
    if (chosenLimit === 0)
        return 0;
    const idx = limits.findIndex((l) => l >= chosenLimit);
    const row = idx === -1 ? rates[rates.length - 1] : rates[idx];
    return row?.[configIndex] ?? 0;
}
/** Look up a flat per-family premium (maternity). */
function flatRate(limits, premiums, chosenLimit) {
    if (chosenLimit === 0)
        return 0;
    const idx = limits.findIndex((l) => l >= chosenLimit);
    return idx === -1 ? (premiums[premiums.length - 1] ?? 0) : (premiums[idx] ?? 0);
}
/**
 * Config index mapping: M→0, M+1→1, M+2→2, M+3→3, M+4→4, M+5→5, AdditionalDep→6
 */
function categoryBenefitPremium(cat, rates) {
    const p = cat.population;
    const lim = cat.limits;
    // Counts by family config
    const configs = [
        [p.M, 0],
        [p.Mplus1, 1],
        [p.Mplus2, 2],
        [p.Mplus3, 3],
        [p.Mplus4, 4],
        [p.Mplus5, 5],
        [p.additionalDependents, 6],
    ];
    let inpatient = 0;
    let outpatient = 0;
    let dental = 0;
    let optical = 0;
    for (const [count, cfgIdx] of configs) {
        if (count === 0)
            continue;
        inpatient += count * tableRate(rates.inpatient.limits, rates.inpatient.rates, lim.inpatientKes, cfgIdx);
        outpatient += count * tableRate(rates.outpatient.limits, rates.outpatient.rates, lim.outpatientKes, cfgIdx);
        dental += count * tableRate(rates.dental.limits, rates.dental.rates, lim.dentalKes, cfgIdx);
        optical += count * tableRate(rates.optical.limits, rates.optical.rates, lim.opticalKes, cfgIdx);
    }
    // Maternity is a flat per-family premium (independent of family config)
    const families = p.M + p.Mplus1 + p.Mplus2 + p.Mplus3 + p.Mplus4 + p.Mplus5;
    const maternity = families * flatRate(rates.maternity.limits, rates.maternity.premiums, lim.maternityKes);
    const lives = p.M * 1 +
        p.Mplus1 * 2 +
        p.Mplus2 * 3 +
        p.Mplus3 * 4 +
        p.Mplus4 * 5 +
        p.Mplus5 * 6 +
        p.additionalDependents;
    return { inpatient, outpatient, dental, optical, maternity, lives, families };
}
export function priceGroupMedical(input, rates) {
    let inpatient = 0;
    let outpatient = 0;
    let dental = 0;
    let optical = 0;
    let maternity = 0;
    let totalLives = 0;
    for (const cat of input.categories) {
        const p = categoryBenefitPremium(cat, rates);
        inpatient += p.inpatient;
        outpatient += p.outpatient;
        dental += p.dental;
        optical += p.optical;
        maternity += p.maternity;
        totalLives += p.lives;
    }
    const grossMedical = inpatient + outpatient + dental + optical + maternity;
    // Co-payment discount reduces the gross medical premium
    const coPaymentDiscount = input.coPayments
        ? grossMedical * rates.coPaymentDiscountPct
        : 0;
    const netMedical = grossMedical - coPaymentDiscount;
    // Levies on net medical premium
    const levies = netMedical * rates.levies.trainingAndPolicyHolderFundPct;
    // SMART virtual card fee; only applies when SMART management is selected and not already enrolled
    const smartFees = input.coverBenefitManagement === "SMART" && !input.alreadyOnSmart
        ? totalLives * rates.smartFeePerLifeKes
        : 0;
    // Critical Illness extension
    const ciPremium = input.criticalIllnessExtension
        ? (() => {
            const pct = (input.averageAgeBand &&
                rates.criticalIllness.ratePctByAgeBand[input.averageAgeBand]) ??
                0;
            const totalCiLimit = input.categories.reduce((sum, cat) => sum + cat.limits.criticalIllnessKes, 0);
            return totalCiLimit * pct;
        })()
        : 0;
    // Group Personal Accident extension
    const gpaPremium = input.groupPersonalAccidentExtension
        ? input.categories.reduce((sum, cat) => sum +
            cat.limits.personalAccidentKes *
                rates.groupPersonalAccident.ratePct *
                cat.limits.numberWithSpouses, 0)
        : 0;
    const items = [
        { code: "INPATIENT", label: "Inpatient Premium", amountKes: inpatient },
        { code: "OUTPATIENT", label: "Outpatient Premium", amountKes: outpatient },
        { code: "DENTAL", label: "Dental Premium", amountKes: dental },
        { code: "OPTICAL", label: "Optical Premium", amountKes: optical },
        { code: "MATERNITY", label: "Maternity Premium", amountKes: maternity },
        ...(coPaymentDiscount > 0
            ? [
                {
                    code: "COPAYMENT_DISCOUNT",
                    label: "Co-payment Discount (10%)",
                    amountKes: -coPaymentDiscount,
                },
            ]
            : []),
        {
            code: "CARD_FEES",
            label: "SMART Card Fees",
            amountKes: smartFees,
            note: input.coverBenefitManagement !== "SMART"
                ? "Waived (manual management)"
                : input.alreadyOnSmart
                    ? "Waived (already on SMART)"
                    : undefined,
        },
        {
            code: "CRITICAL_ILLNESS",
            label: "Critical Illness Extension",
            amountKes: ciPremium,
        },
        {
            code: "GROUP_PA",
            label: "Group Personal Accident Extension",
            amountKes: gpaPremium,
        },
    ];
    return buildQuote({
        product: "group_medical",
        ratesVersion: rates.version,
        items,
        leviesKes: levies,
        stampDutyKes: rates.stampDutyKes,
    });
}
